import React from 'react';
import styled from 'styled-components';
import State, { StateCodes } from '@sunbit-dev/common/enums/State';
import cardCaption from '../../../assets/card-apply/cardNewDesign.svg';
import { Button, ButtonColor, ButtonType } from '../../../ui/button/Button';
import Input2, { InputBackground } from '../../../ui/input/Input2';
import CommonSection from '../../components/commonSection/CommonSection';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import Title from '../../components/title/Title';
import isEmailValid from '../../../../helpers/email';
import { Dropdown } from '../../../ui/dropdown/Dropdown';
import TextArea from '../../../ui/textarea/TextArea';
import checkMark from '../../../assets/card-apply/icons/check-mark.png';

const statesOptions = Object.keys(State).map((state: StateCodes) => ({ value: state as string, label: State[state] }));

// ############################ Styles ############################
// tslint:disable:variable-name
const SContactUs = styled.div`
  display: flex;
  flex-direction: column;
`;

const SPageTitle = styled(Title)`
  white-space: nowrap;
`;

const SLayoutPartition = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  flex: 1;
  @media only screen and ${theme.device.laptop} {
    flex-direction: row;
    & + & {
      margin-left: ${theme.spacing.large}px;
    }
  }
`
);

const SLayoutColumn = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 0;
  position: relative;
  flex: 1;
  @media only screen and ${theme.device.laptop} {
    & + & {
      margin-left: 20px;
    }
  }
`
);

const SLayoutColumn2 = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;;
  flex-grow: 1;
  flex-basis: 0;
  position: relative;
  flex: 1;
  @media only screen and ${theme.device.laptop} {
    flex-direction: column;
    & + & {
      margin-left: 20px;
    }
  }
`
);

const SCommonSection2 = styled(CommonSection)(
  ({ theme }) => `
  @media only screen and ${theme.device.laptop} {
    margin-bottom: ${theme.spacing.large}px;
  }
`
);

const SCommonSection3 = styled(CommonSection)(
  ({ theme }) => `
  @media only screen and ${theme.device.laptop} {
    margin-bottom: ${theme.spacing.larger}px;
  }
`
);

const SMediumInput = styled(Input2)(
  ({ theme }) => `
  max-width: 400px;
  @media only screen and ${theme.device.laptop} {
    margin-bottom: 0;
  }
`
);

const SSmallInput = styled(Input2)(
  () => `
  max-width: 190px;
  margin-right: 20px;
  :last-child {
    margin-right:0px;
  }

`
);

const SCardImage = styled.img(
  ({ theme }) => `
  width: 100%;
  max-width: 450px;
  @media only screen and ${theme.device.laptop} {
    max-width: 518px;
    position: absolute;
    right: 0;
    transform: translate(110%,5%);
  }

  @media only screen and ${theme.device.max.laptop} {
  margin-bottom: 20px;
  }
`
);

const SSubmitButton = styled(Button)(() => '');

const SDropdown = styled(Dropdown)(
  () => `
  max-width: 400px;
`
);

const STextArea = styled(TextArea)(
  () => `
  max-width: 400px;
`
);

const SInputGroupSection = styled.div(() => ({
  display: 'flex',
}));

const SContactUsInputSection = styled.form(
  ({ theme }) => `
  display:flex;
  flex-direction:column;
  margin-bottom: ${theme.spacing.large}px;
`
);

const SCheckMarkIconWrap = styled.section(
  ({ theme }) => `
  text-align: center;
  @media only screen and ${theme.device.laptop} {
    text-align: initial;
    margin-top: ${theme.spacing.huge}px;
  }
`
);

const SCheckMarkIcon = styled.img`
  width: 91px;
`;

const SThankYouWrap = styled.section(
  ({ theme }) => `
  text-align: center;
  @media only screen and ${theme.device.laptop} {
    text-align: initial;
  }
`
);

const SSubtitleDone = styled(SectionTitle)`
  max-width: 450px;
`;

// tslint:enable:variable-name
// ############################ End Styles ###########################

export enum ContactUsErrors {
  invalidEmailAddress = 'invalidEmailAddress',
}

export interface IContactUsState {
  email: string;
  firstName: string;
  lastName: string;
  state: string;
  comments: string;
  phone: string;
  emailError: string;
}

interface IContactUsProps {
  isDone: boolean;
  // values: IContactUsRequest;
  submit: (values: IContactUsState) => void;
  error?: ContactUsErrors | null;
}

// tslint:disable-next-line:variable-name
const ContactUs: React.FC<IContactUsProps> = (props) => {
  const [values, setValues] = React.useState<IContactUsState>({
    email: '',
    emailError: '',
    firstName: '',
    lastName: '',
    phone: '',
    comments: '',
    state: '',
  });

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const newValues: Partial<IContactUsState> = {};
      if (name === 'email') {
        newValues.email = value;
        newValues.emailError = isEmailValid(value) || !value ? '' : 'Invalid Email';
      } else {
        newValues[name as keyof IContactUsState] = value;
      }
      setValues({ ...values, ...newValues });
    },
    [values]
  );

  const handleOnChange = React.useCallback(
    (value, name) => {
      setValues({ ...values, [name]: value });
    },
    [values]
  );

  const isSubmitEnabled = React.useCallback(
    () => Boolean(values.email) && Boolean(values.firstName) && Boolean(values.lastName) && Boolean(values.comments) && Boolean(values.phone) && !values.emailError,
    [values.emailError, values.email, values.firstName, values.lastName, values.comments, values.phone]
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitEnabled()) {
      props.submit({ ...values });
    }
  };

  return (
    <SContactUs>
      <SLayoutPartition>
        <SLayoutColumn>
          {props.isDone === false && (
            <>
              <SCommonSection2>
                <SPageTitle data-at="sunbit-card-title">We&apos;re here to help.</SPageTitle>
              </SCommonSection2>
              <SCommonSection2>
                <SectionTitle data-at="info-intro">For any inquiries, please call us at 855-678-6248 or fill out the contact form below and we&apos;ll be in touch soon.</SectionTitle>
              </SCommonSection2>
            </>
          )}
          <SCardImage src={cardCaption} alt="card caption" />
          {props.isDone && (
            <SCommonSection2>
              <SCheckMarkIconWrap>
                <SCheckMarkIcon src={checkMark} alt="check mark icon" />
              </SCheckMarkIconWrap>
            </SCommonSection2>
          )}
        </SLayoutColumn>
        <SLayoutColumn />
      </SLayoutPartition>
      {props.isDone && (
        <SLayoutColumn>
          <SThankYouWrap>
            <SCommonSection2>
              <Title data-at="sunbit-card-title">Thank you!</Title>
            </SCommonSection2>
            <SCommonSection2>
              <SSubtitleDone data-at="info-intro">Thanks for contacting us! We will get in touch with you shortly.</SSubtitleDone>
            </SCommonSection2>
          </SThankYouWrap>
        </SLayoutColumn>
      )}
      {props.isDone === false && (
        <SLayoutColumn2>
          <SContactUsInputSection data-at="contactUsInputSection" onSubmit={handleSubmit}>
            <SCommonSection3 data-at="namesGroup">
              <SInputGroupSection>
                <SSmallInput color={'primary'} onChange={handleInputChange} dataAt="firstName" name="firstName" value={values.firstName} placeholder="First Name" background={InputBackground.White} />
                <SSmallInput color={'primary'} onChange={handleInputChange} dataAt="lastName" name="lastName" value={values.lastName} placeholder="Last Name" background={InputBackground.White} />
              </SInputGroupSection>
            </SCommonSection3>
            <SCommonSection3 data-at="emailInputGroup">
              <SMediumInput
                color="primary"
                placeholder="Email address"
                name="email"
                dataAt="email"
                value={values.email}
                onChange={handleInputChange}
                background={InputBackground.White}
                error={values.emailError}
              />
            </SCommonSection3>
            <SCommonSection3 data-at="phoneInputGroup">
              <SMediumInput
                color="primary"
                className="inputWithSpacing"
                dataAt="phone"
                name="phone"
                value={values.phone}
                placeholder="Phone"
                background={InputBackground.White}
                onChange={handleInputChange}
              />
            </SCommonSection3>
            <SCommonSection3 data-at="stateInputGroup">
              <SDropdown inputColor={'primary'} className="inputInGroup" dataAt="state" name="state" dropdownOptions={statesOptions} value={values.state as StateCodes} onChange={handleOnChange} />
            </SCommonSection3>
            <SCommonSection3 data-at="commentsInputGroup">
              <STextArea name="comments" value={values.comments} dataAt="comments" placeholder="Additional Comments / Questions" onChange={handleInputChange} />
            </SCommonSection3>
            <SCommonSection3 data-at="submitButtonInputGroup">
              <SSubmitButton type={ButtonType.Submit} dataAt="submit" disabled={!isSubmitEnabled()} color={ButtonColor.primaryLightBlue}>
                Get in touch
              </SSubmitButton>
            </SCommonSection3>
          </SContactUsInputSection>
        </SLayoutColumn2>
      )}
    </SContactUs>
  );
};

export default ContactUs;
